<script setup>
import { ref, onMounted } from 'vue'
import PrimaryButton from '@/components/general/PrimaryButton.vue'
import { ROUTES } from '@/router/routeNames'
import { useAuthStore } from '@/stores/authStore'
import { storeToRefs } from 'pinia'
import mixpanel from '@/services/mixpanel';

const authStore = useAuthStore()
const {user} = storeToRefs(authStore)
const heroRef = ref(null)
const currentSlide = ref(0)
const heroBackgrounds = [
  'bg-gradient-to-br from-green-500 to-green-700',
  'bg-gradient-to-br from-orange-500 to-red-700',
  'bg-gradient-to-br from-blue-500 to-purple-700',
  'bg-gradient-to-br from-green-500 to-red-700',
]

const heroImages = [
  'https://res.cloudinary.com/dpdixbfxz/image/upload/v1746671820/hero-1_chutu9.jpg',
  'https://res.cloudinary.com/dpdixbfxz/image/upload/v1746671543/cld-sample-4.jpg',
  'https://res.cloudinary.com/dpdixbfxz/image/upload/v1746672133/hero-2_ffjxhb.jpg',
  'https://res.cloudinary.com/dpdixbfxz/image/upload/v1746672133/hero-4_snc1al.jpg',
  'https://res.cloudinary.com/dpdixbfxz/image/upload/v1746672136/hero-3_vh7ysb.jpg',
  'https://res.cloudinary.com/dpdixbfxz/image/upload/v1746672138/hero-5_x1jvpf.jpg',
]

const nextSlide = () => {
  currentSlide.value = (currentSlide.value + 1) % heroImages.length
}

const prevSlide = () => {
  currentSlide.value = (currentSlide.value - 1 + heroImages.length) % heroImages.length
}

onMounted(() => {
  // Auto-rotate hero images
  setInterval(() => {
    nextSlide()
  }, 5000)
})
</script>

<template>
  <section ref="heroRef" class="overflow-hidden relative h-[800px]">
    <!-- Hero Background Gradient Carousel -->
    <div class="absolute inset-0 transition-all duration-1000 ease-in-out">
      <div
        v-for="(bgClass, index) in heroBackgrounds"
        :key="index"
        class="absolute inset-0 transition-opacity duration-1000"
        :class="bgClass"
        :style="{ opacity: index === currentSlide ? 1 : 0 }"
      ></div>
      <div class="absolute inset-0 z-10">
        <img :src="heroImages[currentSlide]" class="object-cover w-full h-full" />
      </div>
    </div>

    <!-- Carousel Navigation -->
    <div class="flex absolute right-0 left-0 bottom-10 z-20 justify-center items-center space-x-3">
      <button @click="prevSlide" class="p-1 text-white rounded-full bg-black/30 hover:bg-black/50">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>
      <div class="flex space-x-2">
        <button
          v-for="(_, index) in heroImages"
          :key="index"
          @click="currentSlide = index"
          :class="[index === currentSlide ? 'bg-white' : 'bg-white/50', 'w-3 h-3 rounded-full']"
        ></button>
      </div>
      <button @click="nextSlide" class="p-1 text-white rounded-full bg-black/30 hover:bg-black/50">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
    <div class="relative z-20 py-32">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div class="text-center hero-content">
          <h1
            class="mb-6 text-4xl font-black tracking-tight text-white drop-shadow-lg sm:text-5xl md:text-6xl"
          >
            Favorite Foods, Groceries, and More Delivered to Your Doorstep
          </h1>
          <p class="mx-auto mb-8 max-w-3xl text-xl font-medium sm:text-2xl text-white/90">
            Order fresh produce, groceries, and food from local businesses in your area.
          </p>
          <div class="flex flex-col gap-4 justify-center sm:flex-row">
            <template v-if="!user">
              <PrimaryButton :to="{ name: ROUTES.REGISTER }" as="link" type="primary" @click="mixpanel.track('Get Started was clicked from Hero Section')">
                Get Started
              </PrimaryButton>

              <PrimaryButton :to="{ name: ROUTES.LOGIN }" as="link" type="secondary" @click="mixpanel.track('Sign In was clicked from Hero Section')">
                Sign In
              </PrimaryButton>
            </template>
            <template v-else>
              <PrimaryButton :to="{ name: ROUTES.DASHBOARD }" as="link" type="primary" @click="mixpanel.track('Go to Dashboard was clicked from Hero Section')">
                Go to Dashboard
              </PrimaryButton>
            </template>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
